import React from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { 
  User, 
  ClipboardList, 
  Activity, 
  Phone, 
  Mail, 
  Calendar, 
  Clock, 
  ArrowRight, 
  ShieldAlert, 
  ShieldCheck, 
  Stethoscope, 
  Video, 
  AlertTriangle, 
  Timer, 
  Info,
  Sparkles
} from "lucide-react";
import { motion } from "framer-motion";
import { TeleconsultCardProps } from "../interface/consult";

const TeleconsultCard: React.FC<TeleconsultCardProps> = ({
  teleconsultHistory,
}) => {
  const navigate = useNavigate();

  const refNumber = teleconsultHistory?.participants?.subscriber?.[0]?.ref_number;
  const consultId = teleconsultHistory?.id || "N/A";
  const subscriber = teleconsultHistory?.participants?.subscriber?.[0]?.participant_info;
  const subscriberName = subscriber?.name || "Unknown Patient";
  const consultStatus = teleconsultHistory?.consult_status?.name || "Unknown";

  const handleNavigate = (section: string) => {
    switch (section) {
      case "Summary":
        const publisherToken = teleconsultHistory?.participants?.publisher?.[0]?.token;
        if (publisherToken) navigate(`/teleconsultsummary/${publisherToken}`);
        break;
      case "Health":
        if (refNumber) navigate(`/HealthVitals/${refNumber}`);
        break;
      case "Status":
        const statusToken = teleconsultHistory?.participants?.publisher?.[0]?.token;
        if (statusToken) {
          window.dispatchEvent(new CustomEvent('open-in-app-call', { 
            detail: { 
              url: `https://teleconsult.a2ztelehealth.com/teleconsult-v3/${statusToken}`,
              patientName: subscriberName,
              patientId: refNumber
            } 
          }));
        }
        break;
      default:
        break;
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "New":
        return { 
          color: "text-blue-600", 
          bg: "bg-blue-100/50", 
          dot: "bg-blue-500",
          border: "border-blue-200", 
          icon: <ShieldAlert size={14} />, 
          label: "FRESH",
          btnLabel: "Start",
          btnColor: "bg-blue-600 hover:bg-blue-700 shadow-blue-500/20",
          btnIcon: <PlusIcon size={14} />,
          action: "Status",
          cardBorder: "hover:border-blue-400/60 hover:shadow-blue-500/10",
          avatarBg: "bg-blue-500/10 text-blue-600 border border-blue-500/20"
        };
      case "Waiting":
        return { 
          color: "text-indigo-600", 
          bg: "bg-indigo-100/50", 
          dot: "bg-indigo-500",
          border: "border-indigo-200", 
          icon: <Timer size={14} />, 
          label: "WAITING",
          btnLabel: "Join Room",
          btnColor: "bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/20",
          btnIcon: <Video size={14} />,
          action: "Status",
          cardBorder: "hover:border-indigo-400/60 hover:shadow-indigo-500/10",
          avatarBg: "bg-indigo-500/10 text-indigo-600 border border-indigo-500/20"
        };
      case "Started":
        return { 
          color: "text-amber-600", 
          bg: "bg-amber-100/50", 
          dot: "bg-amber-500",
          border: "border-amber-200", 
          icon: <Activity size={14} />, 
          label: "LIVE",
          btnLabel: "Resume",
          btnColor: "bg-amber-500 hover:bg-amber-600 shadow-amber-500/20",
          btnIcon: <Video size={14} />,
          action: "Status",
          cardBorder: "hover:border-amber-400/65 hover:shadow-amber-500/20 hover:shadow-[0_0_35px_rgba(245,158,11,0.15)]",
          avatarBg: "bg-amber-500/10 text-amber-600 border border-amber-500/20"
        };
      case "Ended":
        return { 
          color: "text-emerald-600", 
          bg: "bg-emerald-100/50", 
          dot: "bg-emerald-500",
          border: "border-emerald-200", 
          icon: <ShieldCheck size={14} />, 
          label: "COMPLETED",
          btnLabel: "View Report",
          btnColor: "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20",
          btnIcon: <ClipboardList size={14} />,
          action: "Summary",
          cardBorder: "hover:border-emerald-400/60 hover:shadow-emerald-500/10",
          avatarBg: "bg-emerald-500/10 text-emerald-600 border border-emerald-500/20"
        };
      case "Cancelled":
        return { 
          color: "text-rose-600", 
          bg: "bg-rose-100/50", 
          dot: "bg-rose-500",
          border: "border-rose-200", 
          icon: <ShieldAlert size={14} />, 
          label: "CANCELLED",
          btnLabel: "Cancelled",
          btnColor: "bg-slate-100 text-slate-400 cursor-not-allowed",
          btnIcon: <XCircle size={14} />,
          action: null,
          cardBorder: "hover:border-rose-300",
          avatarBg: "bg-rose-500/10 text-rose-600 border border-rose-500/20"
        };
      case "Failed":
        return { 
          color: "text-slate-600", 
          bg: "bg-slate-200/50", 
          dot: "bg-slate-500",
          border: "border-slate-300", 
          icon: <AlertTriangle size={14} />, 
          label: "FAILED",
          btnLabel: "Failed",
          btnColor: "bg-slate-100 text-slate-400 cursor-not-allowed",
          btnIcon: <AlertTriangle size={14} />,
          action: null,
          cardBorder: "hover:border-slate-350",
          avatarBg: "bg-slate-500/10 text-slate-600 border border-slate-500/20"
        };
      default:
        return { 
          color: "text-slate-500", 
          bg: "bg-slate-50", 
          dot: "bg-slate-400",
          border: "border-slate-100", 
          icon: <Info size={14} />, 
          label: status.toUpperCase(),
          btnLabel: "Details",
          btnColor: "bg-primary",
          btnIcon: <ArrowRight size={14} />,
          action: "Summary",
          cardBorder: "hover:border-indigo-400/40 hover:shadow-indigo-500/5",
          avatarBg: "bg-slate-100 text-slate-600 border border-slate-200"
        };
    }
  };

  const PlusIcon = ({ size }: { size: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
  );

  const XCircle = ({ size }: { size: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
  );

  const status = getStatusConfig(consultStatus);

  const initials = subscriberName
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <motion.div 
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`bg-white rounded-[2.5rem] border border-slate-100 shadow-[0_10px_30px_-5px_rgba(0,0,0,0.02)] hover:shadow-[0_20px_40px_-5px_rgba(99,102,241,0.08)] transition-all duration-500 flex flex-col group h-full relative overflow-hidden ${status.cardBorder}`}
    >
      {/* Floating Status Accent Bar */}
      <div className={`absolute top-8 left-0 w-1 h-16 rounded-r-full ${status.dot} opacity-80 transition-all duration-500`} />

      <div className="p-6 md:p-8 pl-9 md:pl-11 flex-grow flex flex-col justify-between">
        <div>
          {/* Header Row */}
          <div className="flex justify-between items-start mb-5">
            <div className="flex items-center gap-4 overflow-hidden w-full">
              <div className="relative shrink-0">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-black text-sm md:text-base tracking-wider shadow-sm group-hover:scale-105 transition-all duration-500 ${status.avatarBg}`}>
                  {initials}
                </div>
                <div className={`absolute -top-0.5 -right-0.5 w-3 h-3 ${status.dot} border-2 border-white rounded-full shadow-md animate-pulse`} />
              </div>
              
              <div className="overflow-hidden">
                <h3 className="text-base md:text-lg font-black text-slate-900 mb-1 tracking-tight group-hover:text-indigo-600 transition-colors truncate">
                  {subscriberName}
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full ${status.bg} ${status.color} text-[8px] md:text-[9px] font-black uppercase tracking-widest border ${status.border} whitespace-nowrap`}>
                    {status.icon}
                    {status.label}
                  </span>
                  <span className="text-[8px] md:text-[9px] font-extrabold text-slate-400 uppercase tracking-widest truncate">
                    ID: {String(consultId).slice(0, 8)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Clinical Speech-Bubble block */}
          <div className="bg-gradient-to-br from-slate-50/70 to-slate-100/30 group-hover:from-indigo-50/30 group-hover:to-violet-50/20 rounded-3xl p-4 md:p-5 mb-5 border border-slate-150/40 group-hover:border-indigo-100/30 transition-all duration-500 relative">
            <div className="flex items-start gap-2 mb-3">
              <div className="p-1 rounded-lg bg-indigo-500/10 text-indigo-500 shrink-0">
                <Stethoscope size={13} className="animate-pulse" />
              </div>
              <p className="text-[11px] md:text-[12px] font-bold text-slate-650 group-hover:text-slate-800 leading-relaxed italic line-clamp-2">
                 "{teleconsultHistory?.reason || "General clinical consultation."}"
              </p>
            </div>
            
            <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-200/40">
               <div className="flex items-center gap-1.5">
                  <Calendar className="text-indigo-500 w-3 h-3 shrink-0" />
                  <span className="text-[10px] font-bold text-slate-500">
                    {teleconsultHistory?.scheduled_at ? format(new Date(teleconsultHistory.scheduled_at), 'dd MMM yyyy') : "N/A"}
                  </span>
               </div>
               <div className="h-3 w-px bg-slate-200" />
               <div className="flex items-center gap-1.5">
                  <Clock className="text-indigo-500 w-3 h-3 shrink-0" />
                  <span className="text-[10px] font-bold text-slate-500">
                    {teleconsultHistory?.scheduled_at ? format(new Date(teleconsultHistory.scheduled_at), 'hh:mm a') : "N/A"}
                  </span>
               </div>
            </div>
          </div>
        </div>

        {/* Contact Snippets */}
        <div className="flex flex-wrap items-center gap-2 text-[9px] md:text-[10px] font-extrabold text-slate-400 px-0.5 overflow-hidden">
           <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-slate-50 border border-slate-100/70 hover:text-indigo-650 hover:border-indigo-200 hover:bg-indigo-50/50 transition-all duration-300 cursor-pointer truncate">
              <Phone className="shrink-0 w-3 h-3 text-slate-400 group-hover:text-indigo-500" />
              <span>{subscriber?.phone || "Private"}</span>
           </div>
           <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-slate-50 border border-slate-100/70 hover:text-indigo-650 hover:border-indigo-200 hover:bg-indigo-50/50 transition-all duration-300 cursor-pointer truncate">
              <Mail className="shrink-0 w-3 h-3 text-slate-400 group-hover:text-indigo-500" />
              <span>{subscriber?.email || "Private"}</span>
           </div>
        </div>
      </div>

      {/* Action Suite - Luxurious Dual-Layer Layout */}
      <div className="p-4 bg-slate-50 border-t border-slate-100/60 flex flex-col gap-3 group-hover:bg-slate-50/80 transition-all duration-500">
         {/* Row 1: Primary Action Button (Full Width) */}
         <button 
           onClick={() => status.action && handleNavigate(status.action)}
           disabled={!status.action}
           className={`w-full h-11 md:h-12 rounded-2xl font-black text-[10px] md:text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all shadow-sm active:scale-[0.98] ${
             status.action 
               ? `${status.btnColor} text-white hover:shadow-md cursor-pointer` 
               : "bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed"
           }`}
         >
           {status.btnIcon}
           <span>{status.btnLabel}</span>
         </button>

         {/* Row 2: Secondary Quick Actions (2 Equal Columns) */}
         <div className="grid grid-cols-2 gap-2">
           {/* 1. Clinical Summary */}
           <button 
             onClick={() => handleNavigate("Summary")}
             className="h-10 rounded-xl bg-white border border-slate-200 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50/30 hover:shadow-sm active:scale-95 transition-all flex flex-col items-center justify-center gap-0.5 cursor-pointer"
             title="Clinical Summary"
           >
             <ClipboardList className="w-3.5 h-3.5 text-slate-400" />
             <span className="text-[8px] font-black uppercase tracking-wider text-slate-500">Summary</span>
           </button>
           
           {/* 2. Biometrics */}
           <button 
             onClick={() => handleNavigate("Health")}
             className="h-10 rounded-xl bg-white border border-slate-200 hover:border-emerald-200 hover:text-emerald-600 hover:bg-emerald-50/30 hover:shadow-sm active:scale-95 transition-all flex flex-col items-center justify-center gap-0.5 cursor-pointer"
             title="Patient Biometrics"
           >
             <Activity className="w-3.5 h-3.5 text-emerald-500" />
             <span className="text-[8px] font-black uppercase tracking-wider text-slate-500">Vitals</span>
           </button>
         </div>
      </div>
    </motion.div>
  );
};

export default TeleconsultCard;
